import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = {
  "data-test": "my-reports-table",
  class: "table table-striped",
  style: {"background-color":"white"}
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("thead", null, [
  /*#__PURE__*/_createElementVNode("tr", null, [
    /*#__PURE__*/_createElementVNode("th", { "data-test": "subject-column" }, "Subject"),
    /*#__PURE__*/_createElementVNode("th", { "data-test": "date-completed-column" }, "Date Completed"),
    /*#__PURE__*/_createElementVNode("th"),
    /*#__PURE__*/_createElementVNode("th")
  ])
], -1)
const _hoisted_3 = ["data-test"]
const _hoisted_4 = { "data-test": "report-title" }
const _hoisted_5 = { "data-test": "report-date" }
const _hoisted_6 = ["href"]
const _hoisted_7 = /*#__PURE__*/_createElementVNode("td", null, null, -1)

import moment from "dayjs";
import { computed, onBeforeMount, ref } from "vue";
import { csContext } from "../../services/cs-context";
import { ProfileApiModel, ShortStrengthItemQuestionnaireApiModel } from "../../api-models";
import cms from "../../services/cms/cms";
import { CmsDocumentTypes } from "../../services/cms/cms-type-configuration";

const currentOrgAndTeamIndicatorId = "YgPGAhAAACEA2a6Q";
const initialOrgAndTeamIndicatorId = "ZHXzexEAACgAP6vt"; // used by Priory

interface ReportView {
  id: string;
  title: string;
  url: string;
  dateFormatted: string;
  dateValue: number;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'my-reports-table',
  setup(__props) {

function sortReport(a: ReportView, b: ReportView): number {
  // Descending by completed date
  if (a.dateValue < b.dateValue) {
    return 1;
  }
  if (a.dateValue > b.dateValue) {
    return -1;
  }

  return 0;
}

const profile = ref<ProfileApiModel>(csContext.userProfile);
const questionnaires = ref<ShortStrengthItemQuestionnaireApiModel[]>(
  csContext.userDocument.allCompletedQuestionnairesByMe,
);
const customFrameworkReportTitle = ref<string | null>();

function isSelfReview(report: ShortStrengthItemQuestionnaireApiModel): boolean {
  return report.subjectId === report.reviewerId;
}

function reportLink(report: ShortStrengthItemQuestionnaireApiModel): string {
  return isSelfReview(report) ? `/soloreport/view/${report.id}` : `/report/view/${report.id}`;
}

const customFrameworkReportUrl = computed(() =>
  profile.value?.customFrameworkId
    ? `/customsolo/${profile.value.user.id}|${profile.value.customFrameworkId.toLowerCase()}`
    : null,
);

function dateFormatted(dateToFormat: moment.Dayjs): string {
  return dateToFormat.isValid() ? dateToFormat.format("DD/MM/YYYY") : "";
}

function date(report: ShortStrengthItemQuestionnaireApiModel): string {
  const completedDate = moment(report.completedDate);
  return dateFormatted(completedDate);
}

function subjectFullName(report: ShortStrengthItemQuestionnaireApiModel): string {
  return `${report.subjectDetails.firstName} ${report.subjectDetails.lastName}`;
}

const reports = computed(() => {
  const allReports: ReportView[] = [];

  const completedQuestionnaires = questionnaires.value.filter((q) => q.completedDate);
  allReports.push(
    ...completedQuestionnaires.map((q) => {
      return {
        id: q.id,
        title: isSelfReview(q) ? "Self-review" : subjectFullName(q),
        url: reportLink(q),
        dateFormatted: date(q),
        dateValue: moment(q.completedDate).valueOf(),
      };
    }),
  );

  const shouldDisplayValuesReport =
    profile.value?.customFrameworkId !== currentOrgAndTeamIndicatorId &&
    profile.value?.customFrameworkId !== initialOrgAndTeamIndicatorId;

  if (customFrameworkReportUrl.value && shouldDisplayValuesReport) {
    const latestSoloDate = moment(
      completedQuestionnaires.reduce((latestDate, q) => {
        return q.completedDate! > latestDate ? q.completedDate! : latestDate;
      }, ""),
    );
    const valuesReportNominalDate = latestSoloDate.add(1, "millisecond");
    allReports.push({
      id: "values-report",
      title: customFrameworkReportTitle.value || "Values Report",
      url: customFrameworkReportUrl.value || "",
      dateFormatted: dateFormatted(valuesReportNominalDate),
      dateValue: valuesReportNominalDate.valueOf(),
    });
  }

  return allReports.sort(sortReport);
});

async function getCustomFramework(frameworkId: string): Promise<void> {
  const doc = await cms.getById(CmsDocumentTypes.CmsCustomStrengthMapping, frameworkId);
  customFrameworkReportTitle.value = cms.asText(doc.data?.report_title);
}

onBeforeMount(() => {
  if (profile.value?.customFrameworkId) {
    getCustomFramework(profile.value.customFrameworkId);
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("table", _hoisted_1, [
    _hoisted_2,
    _createElementVNode("tbody", null, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(reports.value, (report, index) => {
        return (_openBlock(), _createElementBlock("tr", {
          "data-test": `row-${index}`,
          key: report.id
        }, [
          _createElementVNode("td", _hoisted_4, [
            _createElementVNode("span", null, _toDisplayString(report.title), 1)
          ]),
          _createElementVNode("td", _hoisted_5, [
            _createElementVNode("span", null, _toDisplayString(report.dateFormatted), 1)
          ]),
          _createElementVNode("td", null, [
            _createElementVNode("a", {
              "data-test": "report-link",
              href: report.url,
              target: "_blank",
              rel: "noopener noreferrer"
            }, " View report ", 8, _hoisted_6)
          ]),
          _hoisted_7
        ], 8, _hoisted_3))
      }), 128))
    ])
  ]))
}
}

})